import * as React from 'react'
import {
  Database,
  DatabaseBackup,
  Download,
  Factory,
  type LucideIcon,
  Terminal,
  Trash2,
  Upload
} from 'lucide-react'

import { cn } from '../../lib/utils'
import { ClearDatabasesCard } from './components/ClearDatabasesCard'
import { LoadFactorySimCard } from './components/LoadFactorySimCard'
import { Neo4jBackupCard } from './components/Neo4jBackupCard'
import { Neo4jBackupImportCard } from './components/Neo4jBackupImportCard'
import { PostgresBackupCard } from './components/PostgresBackupCard'
import { PostgresBackupImportCard } from './components/PostgresBackupImportCard'
import { CypherWriteTab } from './CypherWriteTab'
import { PostgresQueryTab } from './PostgresQueryTab'

export type DatabasesTool =
  | 'neo4j-download'
  | 'neo4j-import'
  | 'postgres-download'
  | 'postgres-import'
  | 'postgres'
  | 'cypher'
  | 'clear'
  | 'factorysim'

const TOOLS: Array<{
  id: DatabasesTool
  label: string
  icon: LucideIcon
}> = [
  { id: 'neo4j-download', label: 'Download Neo4j', icon: Download },
  { id: 'neo4j-import', label: 'Import Neo4j', icon: Upload },
  { id: 'postgres-download', label: 'Download Postgres', icon: DatabaseBackup },
  { id: 'postgres-import', label: 'Import Postgres', icon: Upload },
  { id: 'postgres', label: 'Postgres query', icon: Database },
  { id: 'cypher', label: 'Cypher write', icon: Terminal },
  { id: 'clear', label: 'Clear databases', icon: Trash2 },
  { id: 'factorysim', label: 'Load FactorySim', icon: Factory }
]

/** Map legacy/deep-link advanced panels onto a Databases tool. */
export function databasesToolFromPanel(
  panel: string | null | undefined
): DatabasesTool | undefined {
  if (panel === 'postgres-query') return 'postgres'
  if (panel === 'cypher-write') return 'cypher'
  if (panel === 'databases') return 'neo4j-download'
  return undefined
}

/**
 * Settings → Advanced → Databases: single debug surface for Neo4j/Postgres
 * backup/import, ontology Postgres SQL, Cypher writes, and the destructive clear.
 */
export function DatabasesTab({ initialTool = 'neo4j-download' }: { initialTool?: DatabasesTool }) {
  const [tool, setTool] = React.useState<DatabasesTool>(initialTool)

  React.useEffect(() => {
    setTool(initialTool)
  }, [initialTool])

  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 text-xs text-ink-3">
        Debug tools for the ontology Neo4j graph and application Postgres. Visible only while debug
        mode is enabled.
      </p>

      <div
        role="tablist"
        aria-label="Database tools"
        className="flex flex-wrap gap-1 rounded-lg border border-border/70 bg-muted/40 p-1"
      >
        {TOOLS.map(({ id, label, icon: Icon }) => {
          const active = tool === id
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTool(id)}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
                active
                  ? 'bg-card text-ink shadow-sm'
                  : 'text-ink-3 hover:bg-card/60 hover:text-ink-2',
                id === 'clear' && active ? 'text-st-down' : null
              )}
            >
              <Icon className="size-3.5 shrink-0" strokeWidth={1.75} aria-hidden />
              {label}
            </button>
          )
        })}
      </div>

      <div role="tabpanel" className="min-w-0">
        {tool === 'neo4j-download' ? <Neo4jBackupCard embedded /> : null}
        {tool === 'neo4j-import' ? <Neo4jBackupImportCard embedded /> : null}
        {tool === 'postgres-download' ? <PostgresBackupCard embedded /> : null}
        {tool === 'postgres-import' ? <PostgresBackupImportCard embedded /> : null}
        {tool === 'postgres' ? <PostgresQueryTab embedded /> : null}
        {tool === 'cypher' ? <CypherWriteTab embedded /> : null}
        {tool === 'clear' ? <ClearDatabasesCard embedded /> : null}
        {tool === 'factorysim' ? <LoadFactorySimCard embedded /> : null}
      </div>
    </div>
  )
}
